'use client';

import { memo } from 'react';
import { Locale } from '@fixter/i18n/locale';
import type { CmsImage, CmsNumber, CmsShortText } from '$cms/types';
import { TrustPilot } from '$quantum/atoms/TrustPilot';
import { TRUSTPILOT_TEMPLATE_ID } from '$constants/trustpilot';
import { GoogleReviewsSummary } from '$quantum/molecules/GoogleReviewsSummary';
import { Button, DesignType } from '@/components/atoms/Button';

import styles from './index.module.scss';

export interface GoogleReviewProps {
  readonly googleReviewsStars: CmsNumber;
  readonly googleReviewsTotalReviews: CmsNumber;
  readonly googleReviewsLink: CmsShortText;
  readonly googleReviewsReviewsText: CmsShortText;
  readonly googleReviewsLogo: CmsImage;
}

const defaultLogo = {
  key: 'google-logo',
  url: 'https://res.cloudinary.com/fxtr/image/upload/v1688549036/landing/logos/google_review_fv9hui.svg',
  width: 36,
  height: 31,
  alt: 'Google Logo',
};

const MemoizedGoogleReview = memo(
  ({
    googleReviewsStars,
    googleReviewsTotalReviews,
    googleReviewsLink,
    googleReviewsReviewsText,
    googleReviewsLogo,
  }: GoogleReviewProps) => {
    if (!googleReviewsStars && !googleReviewsTotalReviews && !googleReviewsLink) return null;

    return (
      <GoogleReviewsSummary
        className={styles.googleReviewsSummary}
        rating={googleReviewsStars.number}
        link={googleReviewsLink.value}
        reviewsText={googleReviewsReviewsText.value}
        providerLogo={googleReviewsLogo ?? defaultLogo}
        providerLogoWidth={googleReviewsLogo?.width ?? 36}
        providerLogoHeight={googleReviewsLogo?.height ?? 31}
        shortVersion
        darkTheme
      />
    );
  }
);

MemoizedGoogleReview.displayName = 'MemoizedGoogleReview';

const MemoizedTrustPilot = memo(() => (
  <TrustPilot templateId={TRUSTPILOT_TEMPLATE_ID} height="50px" width="100%" />
));

MemoizedTrustPilot.displayName = 'MemoizedTrustPilot';

interface VrmLookupBlogFooterProps {
  readonly footerButtonText: string;
  readonly footerButtonHref: string;
  readonly locale: Locale;
  readonly googleReviewProps: GoogleReviewProps;
}

export default function VrmLookupBlogFooter({
  locale,
  footerButtonText,
  footerButtonHref,
  googleReviewProps,
}: VrmLookupBlogFooterProps): JSX.Element {
  return (
    <>
      <Button
        designType={DesignType.SECONDARY_DARK}
        href={`tel:${footerButtonHref}`}
        iconId="communication/phone-call-01"
      >
        {footerButtonText}
      </Button>
      <div className={styles.reviewContainer}>
        {locale === Locale.EN_GB ? <MemoizedTrustPilot /> : <MemoizedGoogleReview {...googleReviewProps} />}
      </div>
    </>
  );
}
